import { lazy, Suspense } from "react"

import { Route, Switch } from "react-router-dom"

import AgencyClients from "./Admin/AgencyClients"
import AgencyCompanies from "./Admin/AgencyCompanies"
import AgencyDocuments from "./Admin/AgencyDocuments"
import AgencyMediaTypes from "./Admin/AgencyMediaTypes"
import AgencyTeams from "./Admin/AgencyTeams"
import AgencyVendors from "./Admin/AgencyVendors"
import { Authenticated } from "providers/A12n"
import { AuthorizedRoute } from "components/AuthorizedRoute"
import BookingOrders from "./BookingOrders"
import ClientGroupDetails from "./Admin/ClientGroups/Details"
import ClientGroups from "./Admin/ClientGroups"
import Companies from "./Admin/Company"
import CompanyDetails from "./Admin/Company/Details"
import { Container } from "components/Page"
import GlobalMediaFields from "./Admin/GlobalMediaFields/List"
import GlobalMediaTypes from "./Admin/GlobalMediaTypes"
import { Landing } from "./Landing"
import Layout from "./Layout"
import { Loading } from "components/Loading"
import MarketAgencies from "./Admin/MarketAgencies"
import MarketAgenciesDetails from "./Admin/MarketAgencies/Details"
import MarketClients from "./Admin/MarketClients"
import MarketMediaFields from "./Admin/MarketMediaFields"
import MarketMediaTypes from "./Admin/MarketMediaTypes"
import MarketSettings from "./Admin/MarketSettings"
import MarketTargets from "./Admin/MarketTargets"
import { MarketTeams } from "./Admin/MarketTeams"
import PackageDetails from "./Admin/MarketVendors/Details/RateCard/Packages/Details"
// import PlanningDashboard from "./Dashboards/Planning"
import RateCardDetails from "./Admin/MarketVendors/Details/RateCard/RateCardDetails"
import RegionalDashboard from "./Dashboards/BuyingBilling/Regional"
import Regions from "./Admin/Regions"
import { Reporting } from "./Reporting"
import { RevenueDashboard } from "./Dashboards/RevenueDashboard"
import SupplierDetails from "./Admin/VendorGroups/Details"
import SupplierGroups from "./Admin/VendorGroups"
import Taxes from "./Admin/Taxes"

// const BuyingBilling = lazy(
//   () =>
//     import(
//       /* webpackChunkName: 'BuyingBillingDashboard' */ "./Dashboards/BuyingBilling"
//     ),
// )

// internal route, access only by url
const Labels = lazy(
  () => import(/* webpackChunkName: 'Labels' */ "./Admin/Labels"),
)

// Media Plans
const Plans = lazy(
  () => import(/* webpackChunkName: 'Plans/List' */ "./Plans/List"),
)
const CreatePlan = lazy(
  () => import(/* webpackChunkName: 'Plans/New' */ "./Plans/New"),
)

const PlanDetails = lazy(() => import("./Plans/Details"))
const BookingOrderDetails = lazy(() => import("./Plans/BookingOrderDetails"))

// Campaigns
const Campaigns = lazy(
  () => import(/* webpackChunkName: '/Campaigns/List' */ "./Campaigns/List"),
)

const CampaignDetails = lazy(() => import("./Campaigns/Details"))

//
const Notifications = lazy(
  () =>
    import(/* webpackChunkName: 'Notifications' */ "components/Notifications"),
)

//
const ApproveReject = lazy(() => import("components/ApproveReject"))

const MarketVendors = lazy(() => import("./Admin/MarketVendors"))
const MarketVendorsDetails = lazy(() => import("./Admin/MarketVendors/Details"))
const AgencyVendorsDetails = lazy(() => import("./Admin/AgencyVendors/Details"))

// Billing
const CreateInvoice = lazy(
  () =>
    import(/* webpackChunkName: '/Invoices/New' */ "./Billing/Invoices/New"),
)
const InvoiceDetails = lazy(
  () =>
    import(
      /* webpackChunkName: '/Invoices/Details' */ "./Billing/Invoices/Details"
    ),
)
const Invoices = lazy(
  () =>
    import(/* webpackChunkName: '/Invoices/List' */ "./Billing/Invoices/List"),
)

/** Support **/

const MarketInitialization = lazy(
  () =>
    import(
      /* webpackChunkName: 'MarketInitialization' */ "./Support/MarketInitialization"
    ),
)

const UserManagement = lazy(
  () =>
    import(/* webpackChunkName: 'UserManagement' */ "./Support/UserManagement"),
)

export default function Base() {
  return (
    <Authenticated>
      <Layout>
        <Suspense fallback={<Loading />}>
          <Switch>
            <AuthorizedRoute
              allowed={["AUTH_VIEW_CAMPAIGN"]}
              path="/campaigns/:id"
              component={CampaignDetails}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_LISTING_CAMPAIGN"]}
              path="/campaigns"
              component={Campaigns}
            />
            <AuthorizedRoute
              allowed={["AUTH_CREATE_PLAN"]}
              path="/plans/new"
              component={CreatePlan}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_PLAN"]}
              path="/plans/:id/details/:tab?"
              component={PlanDetails}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_LISTING_PLAN"]}
              path="/plans"
              component={Plans}
            />
            {/* billing */}
            <AuthorizedRoute
              allowed={["AUTH_ACCESS_BILLER"]}
              path="/invoices/new"
              component={CreateInvoice}
            />
            <AuthorizedRoute
              allowed={["AUTH_ACCESS_BILLER"]}
              path="/invoices/:id"
              component={InvoiceDetails}
            />
            <AuthorizedRoute
              allowed={["AUTH_ACCESS_BILLER"]}
              path="/invoices"
              component={Invoices}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_BOOKING_ORDER"]}
              // id below is media plan id
              path="/booking-orders/:id/:boId/:tab?"
              component={BookingOrderDetails}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_BOOKING_ORDER"]}
              path="/booking-orders"
              component={BookingOrders}
            />
            {/* Global Admin  */}
            <AuthorizedRoute
              allowed={["AUTH_VIEW_SUPPLIER_GROUP"]}
              path={`/admin-supplier-groups/:id/:tab?`}
              component={SupplierDetails}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_SUPPLIER_GROUP"]}
              path={`/admin-supplier-groups`}
              component={SupplierGroups}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_MEDIA_TYPE"]}
              path={`/admin-mediaTypes`}
            >
              <Container>
                <GlobalMediaTypes />
              </Container>
            </AuthorizedRoute>
            <AuthorizedRoute
              allowed={["AUTH_VIEW_MEDIA_FIELD"]}
              path={`/admin-mediaFields`}
              component={GlobalMediaFields}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_REGION"]}
              path={`/admin-regions/:tab?`}
              component={Regions}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_CLIENT_GROUP"]}
              path={`/admin-client-groups/:id/:tab?`}
              component={ClientGroupDetails}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_CLIENT_GROUP"]}
              path={`/admin-client-groups`}
              component={ClientGroups}
            />
            <AuthorizedRoute
              allowed={["AUTH_MANAGE_USER_GLOBAL"]}
              path="/admin-labels"
              component={Labels}
            />
            {/* Market */}
            <AuthorizedRoute
              allowed={["AUTH_VIEW_MARKET"]}
              path={`/market-settings`}
              component={MarketSettings}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_COMPANY"]}
              path={`/market-companies/:id/:tab?`}
              component={CompanyDetails}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_COMPANY"]}
              path={`/market-companies`}
              component={Companies}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_CLIENT"]}
              path={`/market-clients`}
              component={MarketClients}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_RATE_CARD"]}
              path="/market-vendors/:id/:tab?"
              component={MarketVendorsDetails}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_RATE_CARD"]}
              path={"/rate-cards/:id"}
              component={RateCardDetails}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_RATE_CARD"]}
              path={"/packages/:id"}
              component={PackageDetails}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_SUPPLIER_LIST", "AUTH_VIEW_SUPPLIER"]}
              path="/market-vendors"
              component={MarketVendors}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_AGENCY"]}
              path={`/market-agencies/:id/:tab?`}
              component={MarketAgenciesDetails}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_AGENCY"]}
              path={`/market-agencies`}
              component={MarketAgencies}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_MEDIA_TYPE"]}
              path="/market-mediatypes"
              component={MarketMediaTypes}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_MEDIA_FIELD"]}
              path="/market-mediafields"
              component={MarketMediaFields}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_USER_MARKET"]}
              path="/market-teams"
              component={MarketTeams}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_TARGET"]}
              path="/market-targets/:tab"
              component={MarketTargets}
            />
            <AuthorizedRoute allowed={["AUTH_VIEW_TAX"]} path="/market-taxes">
              <Taxes queryKeyParam="market" configScope={"MARKET"} />
            </AuthorizedRoute>

            {/* Agency */}
            <AuthorizedRoute
              allowed={["AUTH_VIEW_AGENCY_CLIENT"]}
              path="/agency-companies/:id/:tab?"
              component={CompanyDetails}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_AGENCY_CLIENT"]}
              path="/agency-companies"
              component={AgencyCompanies}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_AGENCY_DOCUMENT"]}
              path="/agency-documents"
              component={AgencyDocuments}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_MEDIA_TYPE"]}
              path="/agency-mediatypes"
              component={AgencyMediaTypes}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_TEAM", "AUTH_VIEW_AGENCY_CLIENT_TEAM"]}
              path="/agency-teams"
              component={AgencyTeams}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_AGENCY_CLIENT"]}
              path="/agency-clients"
              component={AgencyClients}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_SUPPLIER"]}
              path="/agency-vendors/:id/:tab?"
              component={AgencyVendorsDetails}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_SUPPLIER"]}
              path="/agency-vendors"
              component={AgencyVendors}
            />
            <Route path="/result/:action" component={ApproveReject} />
            <Route path="/notifications" component={Notifications} />
            <AuthorizedRoute
              allowed={[
                "AUTH_VIEW_REVENUE_DASHBOARD",
                "AUTH_ACCESS_GLOBAL_FINANCE",
              ]}
              path="/revenue"
              component={RevenueDashboard}
            />
            {/* <AuthorizedRoute
              allowed={["AUTH_VIEW_BUYING_BILLING_DASHBOARD"]}
              path="/buying-billing-dashboard/:summary?"
              component={BuyingBilling}
            /> */}
            {/* <AuthorizedRoute
              allowed={["AUTH_VIEW_PLANNING_DASHBOARD"]}
              path="/planning-dashboard"
              component={PlanningDashboard}
            /> */}
            <AuthorizedRoute
              allowed={["AUTH_VIEW_EXECUTIVE_DASHBOARD"]}
              path="/regional-dashboard"
              component={RegionalDashboard}
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_REPORT_BUILDER"]}
              path="/reporting"
              component={Reporting}
            />

            {/** Support **/}
            <AuthorizedRoute
              allowed={["AUTH_ACCESS_SUPPORT"]}
              path="/support/market-initialization"
              component={MarketInitialization}
            />
            <AuthorizedRoute
              allowed={["AUTH_ACCESS_SUPPORT"]}
              path="/support/user-management"
              component={UserManagement}
            />

            <Route path="/" component={Landing} />
          </Switch>
        </Suspense>
      </Layout>
    </Authenticated>
  )
}
